import {
  useBlotterVisibleRowsEnrichment,
  useMultiCustomerMarketDataSnapshot,
  useMultiMarketDataSnapshot,
  useVisibleCellsChanged,
  type IEnrichableEntity,
  type KeyedMarketDataSnapshot,
  type MinimalSubscriptionResponse,
  type UseMultiCustomerMarketDataSnapshotRequest,
  type UseMultiMarketDataSnapshotRequest,
} from '@talos/kyoko';
import type { Column } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { of, type Observable } from 'rxjs';
import { useDisplaySettings } from '../../../providers/DisplaySettingsProvider';
import { OrgConfigurationKey, useOrgConfiguration } from '../../../providers/OrgConfigurationProvider';
import type { PriceGridEntity } from '../PriceGridEntity';
import {
  combinePriceGridMarketDataEnrichmentOutputs,
  getPriceGridEnrichmentRequestsObs,
  getPriceGridMarketDataEnrichmentLookupKey,
  isPriceGridMarketDataColumnRelevant,
  type PriceGridMarketDataEnrichmentSpec,
} from './enrichment';

const DEFAULT_MAX_SUBSCRIPTIONS = 80;
const EMPTY_CUSTOMER_MARKET_DATA_REQUESTS = of<UseMultiCustomerMarketDataSnapshotRequest[]>([]);

function getMarketDataSnapshotKey(mds: KeyedMarketDataSnapshot) {
  return mds.requestKey;
}

function getPriceGridEntityKey<T extends PriceGridEntity>(item: T) {
  return item.rowID;
}

export function usePriceGridMarketDataEnrichment<
  T extends PriceGridEntity & IEnrichableEntity<T, KeyedMarketDataSnapshot>,
>({
  dataObservable,
  marketDataSpec,
  customerMarketDataSpec,
}: {
  dataObservable: Observable<MinimalSubscriptionResponse<T>>;
  /** Lets a grid own how its rows turn into market data requests instead of reading params off column defs. */
  marketDataSpec: PriceGridMarketDataEnrichmentSpec<UseMultiMarketDataSnapshotRequest>;
  /** Omit when the grid has no customer market data columns. */
  customerMarketDataSpec?: PriceGridMarketDataEnrichmentSpec<UseMultiCustomerMarketDataSnapshotRequest>;
}): {
  gridOptionsOverlay: ReturnType<typeof useVisibleCellsChanged<T>>['gridOptionsOverlay'];
  enrichedDataObservable: Observable<MinimalSubscriptionResponse<T>>;
} {
  const { getConfig } = useOrgConfiguration();
  const specs = useMemo(
    () => (customerMarketDataSpec ? [marketDataSpec, customerMarketDataSpec] : [marketDataSpec]),
    [customerMarketDataSpec, marketDataSpec]
  );
  const { visibleCellsChangedObs, gridOptionsOverlay } = useVisibleCellsChanged<T>({
    getUniqueKey: getPriceGridEntityKey,
    isColumnRelevant: column => isPriceGridMarketDataColumnRelevant({ column, specs }),
  });

  const visibleRequestsObs: Observable<UseMultiMarketDataSnapshotRequest[]> = useMemo(
    () =>
      getPriceGridEnrichmentRequestsObs({
        spec: marketDataSpec,
        visibleCellsChangedObs,
      }),
    [marketDataSpec, visibleCellsChangedObs]
  );
  const visibleCustomerRequestsObs: Observable<UseMultiCustomerMarketDataSnapshotRequest[]> = useMemo(
    () =>
      customerMarketDataSpec
        ? getPriceGridEnrichmentRequestsObs({
            spec: customerMarketDataSpec,
            visibleCellsChangedObs,
          })
        : EMPTY_CUSTOMER_MARKET_DATA_REQUESTS,
    [customerMarketDataSpec, visibleCellsChangedObs]
  );
  const getEnrichmentLookupKey = useCallback(
    ({ item, column }: { item: T; column: Column }) =>
      getPriceGridMarketDataEnrichmentLookupKey({ specs, item, column }),
    [specs]
  );

  const maxActiveSubscriptions = getConfig(
    OrgConfigurationKey.PriceGridMaxActiveSubscriptions,
    DEFAULT_MAX_SUBSCRIPTIONS
  );

  const { showAllInPrices, showFirmLiquidity } = useDisplaySettings();
  const marketDataObs = useMultiMarketDataSnapshot({
    requestsObs: visibleRequestsObs,
    subscriptionTag: 'Price Grid V2',
    maxActiveSubscriptions,
    showAllInPrices: !!showAllInPrices,
    showFirmLiquidity,
  });
  const customerMarketDataObs = useMultiCustomerMarketDataSnapshot({
    requestsObs: visibleCustomerRequestsObs,
    subscriptionTag: 'Customer Price Grid V2',
    maxActiveSubscriptions,
  });

  // Each enrichment stream updates its own cache slice; if only one stream emits, rows still enrich
  // from that cache while the other stream stays empty until it has data.
  const combinedMarketDataObs = useMemo(() => {
    const enrichmentOutputs = [
      {
        specID: marketDataSpec.id,
        observable: marketDataObs,
      },
    ];
    if (customerMarketDataSpec) {
      enrichmentOutputs.push({
        specID: customerMarketDataSpec.id,
        observable: customerMarketDataObs,
      });
    }
    return combinePriceGridMarketDataEnrichmentOutputs(enrichmentOutputs);
  }, [customerMarketDataObs, customerMarketDataSpec, marketDataObs, marketDataSpec.id]);

  const { enrichedDataObservable } = useBlotterVisibleRowsEnrichment<T, KeyedMarketDataSnapshot>({
    dataObservable,
    enrichmentObservable: combinedMarketDataObs,
    visibleCellsChangedObs,
    getUniqueKey: getPriceGridEntityKey,
    getUniqueEnrichmentKey: getMarketDataSnapshotKey,
    getEnrichmentLookupKey,
  });

  return {
    gridOptionsOverlay,
    enrichedDataObservable,
  };
}
